/**
 * parsePemCertificate
 * Decodifica um certificado PEM e retorna todas as informações relevantes.
 * Não depende de bibliotecas externas — usa apenas Web Crypto API (browser) ou
 * o módulo nativo "crypto" do Node.js 18+.
 *
 * @param {string} pemContent  Conteúdo do certificado no formato PEM
 * @returns {object}           Objeto com todos os dados do certificado
 */
function parsePemCertificate(pemContent) {
  // ─── 1. Limpa e extrai o Base64 ───────────────────────────────────────────
  const base64 = pemContent
    .replace(/-----BEGIN CERTIFICATE-----/g, '')
    .replace(/-----END CERTIFICATE-----/g, '')
    .replace(/\s+/g, '');

  if (!base64) {
    throw new Error('Conteúdo PEM inválido ou vazio.');
  }

  // ─── 2. Converte Base64 → bytes (Uint8Array) ──────────────────────────────
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // ─── 3. Parser ASN.1 DER minimalista ─────────────────────────────────────
  let pos = 0;

  function readByte() {
    return bytes[pos++];
  }

  function readLength() {
    let len = readByte();
    if (len & 0x80) {
      const numBytes = len & 0x7f;
      len = 0;
      for (let i = 0; i < numBytes; i++) {
        len = (len << 8) | readByte();
      }
    }
    return len;
  }

  function readTLV() {
    const tag = readByte();
    const len = readLength();
    const value = bytes.slice(pos, pos + len);
    pos += len;
    return { tag, len, value };
  }

  function peekTag() {
    return bytes[pos];
  }

  // Lê OID e converte para string pontilhada (ex: "2.5.4.3")
  function decodeOID(data) {
    let result = '';
    let i = 0;
    const first = data[i++];
    result += Math.floor(first / 40) + '.' + (first % 40);
    let value = 0;
    while (i < data.length) {
      const byte = data[i++];
      value = (value << 7) | (byte & 0x7f);
      if (!(byte & 0x80)) {
        result += '.' + value;
        value = 0;
      }
    }
    return result;
  }

  // Decodifica string ASN.1 (UTF8String, PrintableString, IA5String, BMPString)
  function decodeString(tag, data) {
    if (tag === 0x1e) {
      // BMPString (UTF-16 BE)
      let str = '';
      for (let i = 0; i < data.length; i += 2) {
        str += String.fromCharCode((data[i] << 8) | data[i + 1]);
      }
      return str;
    }
    return new TextDecoder('utf-8').decode(data);
  }

  // Decodifica UTCTime / GeneralizedTime → objeto Date
  function decodeTime(tag, data) {
    const str = new TextDecoder().decode(data);
    let year, month, day, hour, minute, second;
    if (tag === 0x17) {
      // UTCTime: YYMMDDHHMMSSZ
      year   = parseInt(str.slice(0, 2));
      year   += year >= 50 ? 1900 : 2000;
      month  = parseInt(str.slice(2, 4));
      day    = parseInt(str.slice(4, 6));
      hour   = parseInt(str.slice(6, 8));
      minute = parseInt(str.slice(8, 10));
      second = parseInt(str.slice(10, 12));
    } else {
      // GeneralizedTime: YYYYMMDDHHMMSSZ
      year   = parseInt(str.slice(0, 4));
      month  = parseInt(str.slice(4, 6));
      day    = parseInt(str.slice(6, 8));
      hour   = parseInt(str.slice(8, 10));
      minute = parseInt(str.slice(10, 12));
      second = parseInt(str.slice(12, 14));
    }
    return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  }

  // Mapeia OID → nome amigável
  const OID_MAP = {
    '2.5.4.3':              'commonName',
    '2.5.4.4':              'surname',
    '2.5.4.5':              'serialNumber',
    '2.5.4.6':              'countryName',
    '2.5.4.7':              'localityName',
    '2.5.4.8':              'stateOrProvinceName',
    '2.5.4.9':              'streetAddress',
    '2.5.4.10':             'organizationName',
    '2.5.4.11':             'organizationalUnitName',
    '2.5.4.12':             'title',
    '2.5.4.42':             'givenName',
    '2.5.4.97':             'organizationIdentifier',
    '1.2.840.113549.1.9.1': 'emailAddress',
    '2.5.29.17':            'subjectAltName',
    '2.5.29.19':            'basicConstraints',
    '2.5.29.15':            'keyUsage',
    '2.5.29.37':            'extKeyUsage',
    '2.5.29.14':            'subjectKeyIdentifier',
    '2.5.29.35':            'authorityKeyIdentifier',
    '2.5.29.31':            'cRLDistributionPoints',
    '1.3.6.1.5.5.7.1.1':   'authorityInfoAccess',
    '2.5.29.32':            'certificatePolicies',
    '1.2.840.113549.1.1.1': 'rsaEncryption',
    '1.2.840.113549.1.1.5': 'sha1WithRSAEncryption',
    '1.2.840.113549.1.1.11':'sha256WithRSAEncryption',
    '1.2.840.113549.1.1.12':'sha384WithRSAEncryption',
    '1.2.840.113549.1.1.13':'sha512WithRSAEncryption',
    '1.2.840.10045.4.3.2':  'ecdsa-with-SHA256',
    '1.2.840.10045.4.3.3':  'ecdsa-with-SHA384',
    '1.2.840.10045.4.3.4':  'ecdsa-with-SHA512',
    '2.16.840.1.101.3.4.2.1':'sha-256',
    '2.16.840.1.101.3.4.2.2':'sha-384',
    '2.16.840.1.101.3.4.2.3':'sha-512',
  };

  // Lê um DN (Distinguished Name) como objeto
  function readDN(seqBytes) {
    const result = {};
    let i = 0;
    const saved = pos;
    pos = 0;
    const localBytes = seqBytes;

    function lb() { return localBytes[i++]; }
    function ll() {
      let len = lb();
      if (len & 0x80) {
        const n = len & 0x7f; len = 0;
        for (let j = 0; j < n; j++) len = (len << 8) | lb();
      }
      return len;
    }

    while (i < localBytes.length) {
      const setTag = lb(); // SET
      const setLen = ll();
      const setEnd = i + setLen;
      while (i < setEnd) {
        const seqTag = lb(); // SEQUENCE
        const seqLen = ll();
        const seqEnd = i + seqLen;
        const oidTag = lb();
        const oidLen = ll();
        const oidBytes = localBytes.slice(i, i + oidLen); i += oidLen;
        const oid = decodeOID(oidBytes);
        const valTag = lb();
        const valLen = ll();
        const valBytes = localBytes.slice(i, i + valLen); i += valLen;
        const name = OID_MAP[oid] || oid;
        result[name] = decodeString(valTag, valBytes);
        i = seqEnd;
      }
      i = setEnd;
    }
    pos = saved;
    return result;
  }

  // Converte bytes para hex string
  function toHex(data) {
    return Array.from(data).map(b => b.toString(16).padStart(2, '0')).join(':');
  }

  // ─── 4. Navega pela estrutura do certificado X.509 ───────────────────────
  // Certificate ::= SEQUENCE { tbsCertificate, signatureAlgorithm, signature }
  pos = 0;
  readByte(); readLength(); // SEQUENCE raiz

  // tbsCertificate SEQUENCE
  readByte(); const tbsLen = readLength();
  const tbsStart = pos;

  // version [0] EXPLICIT (opcional)
  let version = 1;
  if (peekTag() === 0xa0) {
    readByte(); readLength();
    readByte(); readLength(); // INTEGER
    version = readByte() + 1;
  }

  // serialNumber
  readByte(); const snLen = readLength();
  const snBytes = bytes.slice(pos, pos + snLen); pos += snLen;
  const serialNumber = toHex(snBytes).replace(/^00:/, '');

  // signature algorithm (dentro do tbs)
  readByte(); const sigAlgLen = readLength();
  const sigAlgEnd = pos + sigAlgLen;
  readByte(); const oidLen = readLength();
  const sigOidBytes = bytes.slice(pos, pos + oidLen); pos += oidLen;
  const signatureAlgorithmOID = decodeOID(sigOidBytes);
  const signatureAlgorithm = OID_MAP[signatureAlgorithmOID] || signatureAlgorithmOID;
  pos = sigAlgEnd;

  // issuer
  readByte(); const issuerLen = readLength();
  const issuerBytes = bytes.slice(pos, pos + issuerLen); pos += issuerLen;
  const issuer = readDN(issuerBytes);

  // validity SEQUENCE { notBefore, notAfter }
  readByte(); readLength();
  const notBeforeTag = readByte(); const notBeforeLen = readLength();
  const notBeforeBytes = bytes.slice(pos, pos + notBeforeLen); pos += notBeforeLen;
  const notBefore = decodeTime(notBeforeTag, notBeforeBytes);

  const notAfterTag = readByte(); const notAfterLen = readLength();
  const notAfterBytes = bytes.slice(pos, pos + notAfterLen); pos += notAfterLen;
  const notAfter = decodeTime(notAfterTag, notAfterBytes);

  // subject
  readByte(); const subjectLen = readLength();
  const subjectBytes = bytes.slice(pos, pos + subjectLen); pos += subjectLen;
  const subject = readDN(subjectBytes);

  // subjectPublicKeyInfo
  readByte(); const spkiLen = readLength();
  const spkiEnd = pos + spkiLen;
  readByte(); const pkAlgLen = readLength();
  const pkAlgEnd = pos + pkAlgLen;
  readByte(); const pkOidLen = readLength();
  const pkOidBytes = bytes.slice(pos, pos + pkOidLen); pos += pkOidLen;
  const publicKeyAlgorithmOID = decodeOID(pkOidBytes);
  const publicKeyAlgorithm = OID_MAP[publicKeyAlgorithmOID] || publicKeyAlgorithmOID;
  pos = spkiEnd;

  // extensions [3] (opcional)
  const extensions = {};
  if (pos < tbsStart + tbsLen && peekTag() === 0xa3) {
    readByte(); readLength(); // [3] EXPLICIT
    readByte(); readLength(); // SEQUENCE OF extensions
    const extEnd = tbsStart + tbsLen;
    while (pos < extEnd) {
      if (peekTag() !== 0x30) break;
      readByte(); const extLen = readLength();
      const extContentEnd = pos + extLen;
      // OID
      readByte(); const extOidLen = readLength();
      const extOidBytes = bytes.slice(pos, pos + extOidLen); pos += extOidLen;
      const extOid = decodeOID(extOidBytes);
      const extName = OID_MAP[extOid] || extOid;
      // critical (BOOLEAN, opcional)
      let critical = false;
      if (pos < extContentEnd && peekTag() === 0x01) {
        readByte(); readLength(); critical = readByte() !== 0x00;
      }
      // extnValue OCTET STRING
      if (pos < extContentEnd && peekTag() === 0x04) {
        readByte(); const evLen = readLength();
        const evBytes = bytes.slice(pos, pos + evLen); pos += evLen;
        extensions[extName] = { critical, rawHex: toHex(evBytes).slice(0, 80) + '...' };
      }
      pos = extContentEnd;
    }
  }

  // ─── 5. Calcula status de validade ────────────────────────────────────────
  const now = new Date();
  const isExpired = now > notAfter;
  const isNotYetValid = now < notBefore;
  const daysRemaining = Math.ceil((notAfter - now) / (1000 * 60 * 60 * 24));
  const totalDays = Math.ceil((notAfter - notBefore) / (1000 * 60 * 60 * 24));

  let status;
  if (isExpired)       status = 'EXPIRADO';
  else if (isNotYetValid) status = 'AINDA NÃO VÁLIDO';
  else if (daysRemaining <= 30) status = 'EXPIRANDO EM BREVE';
  else                 status = 'VÁLIDO';

  // ─── 6. Retorna resultado estruturado ────────────────────────────────────
  return {
    version: `v${version}`,
    serialNumber,
    signatureAlgorithm,
    subject,
    issuer,
    validity: {
      notBefore,
      notAfter,
      notBeforeFormatted: notBefore.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }),
      notAfterFormatted:  notAfter.toLocaleString('pt-BR',  { timeZone: 'America/Sao_Paulo' }),
      daysRemaining,
      totalDays,
      status,
      isExpired,
      isNotYetValid,
    },
    publicKeyAlgorithm,
    extensions,
    raw: {
      base64,
      sizeBytes: bytes.length,
    },
  };
}

// ─── Utilitário: formata o resultado para exibição no console ────────────────
function printCertificateInfo(info) {
  console.log('\n╔══════════════════════════════════════════════════╗');
  console.log('║         INFORMAÇÕES DO CERTIFICADO SSL/TLS       ║');
  console.log('╚══════════════════════════════════════════════════╝\n');

  console.log(`  Versão              : ${info.version}`);
  console.log(`  Número de série     : ${info.serialNumber}`);
  console.log(`  Algoritmo assinatura: ${info.signatureAlgorithm}`);
  console.log(`  Algoritmo chave pub : ${info.publicKeyAlgorithm}`);

  console.log('\n  ── Titular (Subject) ──');
  for (const [k, v] of Object.entries(info.subject)) {
    console.log(`  ${k.padEnd(28)}: ${v}`);
  }

  console.log('\n  ── Emissor (Issuer) ──');
  for (const [k, v] of Object.entries(info.issuer)) {
    console.log(`  ${k.padEnd(28)}: ${v}`);
  }

  console.log('\n  ── Validade ──');
  console.log(`  Início              : ${info.validity.notBeforeFormatted}`);
  console.log(`  Vencimento          : ${info.validity.notAfterFormatted}`);
  console.log(`  Dias de validade    : ${info.validity.totalDays}`);
  console.log(`  Dias restantes      : ${info.validity.daysRemaining}`);
  console.log(`  Status              : ${info.validity.status}`);

  if (Object.keys(info.extensions).length) {
    console.log('\n  ── Extensões ──');
    for (const [k] of Object.entries(info.extensions)) {
      const ext = info.extensions[k];
      console.log(`  ${k.padEnd(28)}: critical=${ext.critical}`);
    }
  }

  console.log(`\n  Tamanho do cert.    : ${info.raw.sizeBytes} bytes`);
  console.log('');
}

// ─── Exporta para uso como módulo (Node.js) ──────────────────────────────────
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { parsePemCertificate, printCertificateInfo };
}

// ─── Exemplo de uso ──────────────────────────────────────────────────────────
/*
const pemContent = `-----BEGIN CERTIFICATE-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA... (seu certificado aqui)
-----END CERTIFICATE-----`;

try {
  const info = parsePemCertificate(pemContent);
  printCertificateInfo(info);

  // Verificar se expira em menos de 30 dias
  if (info.validity.daysRemaining <= 30 && !info.validity.isExpired) {
    console.warn(`⚠️  Certificado expira em ${info.validity.daysRemaining} dias!`);
  }

  if (info.validity.isExpired) {
    console.error('❌ Certificado EXPIRADO!');
  }
} catch (err) {
  console.error('Erro ao decodificar certificado:', err.message);
}
*/


// WEBPACK FOOTER //
// ./src/util/DecodeCertificado.js